import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, FileText, Video, Mail, ExternalLink } from 'lucide-react';
import { StepCard, InfoCard } from './SectionComponents';

const StandoutApplicationsSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  const rationaleTips = [
    'Spell-check everything (especially the company name).',
    'Make it specific: reference something concrete from the video pitch or JD.',
    'Show alignment: explain why you’re interested and highlight relevant experience.',
    'Be concise but impactful: focus on achievements, not generic statements.'
  ];

  const whyWatch = [
    'Get a real sense of the culture — video pitches convey the “vibes” JDs don’t.',
    'Learn what matters most to the founders — reuse this in rationale and interviews.',
    'Spot hidden opportunities — exciting roles may not sound obvious on paper.'
  ];

  const outreachTips = [
    'Mention something specific you liked from their video pitch.',
    'Keep it short, polite, and genuine.',
    'Avoid generic copy-paste messages.'
  ];

  const preSubmitChecklist = [
    'I’ve watched the startup’s video pitch.',
    'If there’s a rationale, it’s tailored and spell-checked (including company name).',
    'My application clearly shows why I’m a strong fit.'
  ];

  const rationaleExample = `“I was drawn to Gaia’s mission and the way you articulated it in your spotlight video. My internship at a health insurance startup gave me hands-on exposure to Insurtech, where I saw the need for innovation first-hand. Alongside this, running my own podcast has helped me develop strong project ownership skills, and my data analysis work has placed me in the top 1% nationwide in fantasy football analytics. I’d love the chance to bring these skills to your team.”`;

  const outreachExample = `Hi [Name], I really enjoyed your Jumpstart video pitch and was impressed by your vision for scaling [product]. I’ve just submitted my application for the [role] and would love the chance to learn more about your journey at [startup name].`;

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">How to Make Standout Applications</h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Once your profile, CV and video intro are ready, it’s time to submit standout applications.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">

          <StepCard title="1) Write a winning rationale ✍️">
            <div className="space-y-8">
              <p className="text-gray-700 leading-relaxed">
                Rationale is an additional opportunity to show motivation, communication skills and attention-to-detail. It’s shared directly with the startup — make it count!
              </p>
              <div className="space-y-3">
                {rationaleTips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1" />
                    <span className="text-gray-800 leading-relaxed">{tip}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Example</h4>
                <p className="text-gray-700 leading-relaxed italic">{rationaleExample}</p>
              </div>
            </div>
          </StepCard>

          <StepCard title="2) Use startup video pitch to your advantage 🎥">
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed">
                Most startups record a short video pitch where founders share their mission, culture and what they’re looking for. These are goldmines for stronger applications and interview prep.
              </p>
              <div className="space-y-3">
                {whyWatch.map((tip, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1" />
                    <span className="text-gray-800 leading-relaxed">{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="3) Smart LinkedIn outreach 💌 (optional but powerful)">
            <div className="space-y-8">
              <p className="text-gray-700 leading-relaxed">
                Found a startup you really love? Reaching out directly can make you more memorable — keep it short and personalised.
              </p>
              <div className="space-y-3">
                {outreachTips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1" />
                    <span className="text-gray-800 leading-relaxed">{tip}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Example message</h4>
                <p className="text-gray-700 leading-relaxed italic">{outreachExample}</p>
              </div>
            </div>
          </StepCard>

          <StepCard title="4) Before you apply - a quick checklist ✅">
            <div className="space-y-4">
              {preSubmitChecklist.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-[#02B491] rounded-full text-white font-bold text-xs flex items-center justify-center mt-0.5">{i + 1}</div>
                  <span className="text-gray-800 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white border-2 border-[#02B491] text-[#02B491] rounded-2xl font-semibold hover:bg-[#02B491] hover:text-white transition-all duration-300 shadow-lg"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandoutApplicationsSection;


